import React from 'react';
import FlatButton from 'material-ui/FlatButton';
import Dialog from 'material-ui/Dialog';
import {MdCheckbox} from 'core/components/fields/fields';
import documentService from "core/document/documentService";
import mdEntityService from "core/metamodel/mdEntityService";
import commonService from 'core/common/service/commonService';
import {ErrorComp} from 'core/components/errorComp/errorComp';
import {lovLabel, lovValue} from "core/form/formUtils";
import {
  Step,
  Stepper,
  StepLabel,
} from 'material-ui/Stepper';
import {observable, action} from "mobx";
import Grid from "core/grid/domain/grid";
import {observer} from "mobx-react";
import {DocumentCountDto} from "core/document/documentDomain";
import uiService from "core/common/service/uiService";
import {
  DialogAncestor, DialogProps, entityStatusCss, entityStatusText, SteppingDialogAncestor,
  validationMessageCss
} from "core/common/page/dialogAncestor";
import {Link} from 'react-router';
import {addTenantToQuery} from "core/common/utils/utils";
import {GridBulkEntityDto} from "core/grid/gridUtils";
import gridService from "core/grid/gridService";

interface EntitiesEmailDialogProps extends DialogProps {
  entityName: string;
  documentLocation: string;
  localizedEntitiesName: string;
  entityDetailRoute: string;
}

@observer
export default class EntitiesEmailDialog extends SteppingDialogAncestor<EntitiesEmailDialogProps, undefined, any> {


  @observable private emailEntitiesObject: GridBulkEntityDto;

  @action open(resolve, reject, grid: Grid) {
    let {entities, entityName, documentLocation} = this.props;

    uiService.showToolbarActionFlag();

    let gridParams = grid.createFullParams({
      _includeSent: false,
      _includeAll: false,
      _includeByAll: true,
      _byEmail: true
    });
    console.log('EntitiesEmailDialog: Opening dialog for grid params ', gridParams);

    this.resolve = resolve;
    this.reject = reject;



    Promise.all([mdEntityService.fetchEntityMetadata([entityName]), documentService.documentCount(entityName, documentLocation, gridParams)])
      .then(([entities, documentCountObject]: [any, DocumentCountDto]) => {
        this.emailEntitiesObject = new GridBulkEntityDto({
          entityStatuses: null,
          showErrors: null,
          part: 1,
          gridParams: gridParams,
          includeAll: false,
          includeSent: false,
          count: documentCountObject.documentCount
        });

        this.dialogOpen = true;
        uiService.hideToolbarActionFlag();

      })
      .catch(error => {
        this.reject(error);
      });

  }


  @action prepare = () => {
    const {entityName} = this.props;
    console.log('Preparing email ' + entityName + "s");

    commonService.loading(true);
    gridService.doMultipleEmail(this.emailEntitiesObject, entityName, 1)
      .then(action("prepare-action", (newEmailEntitiesObject: GridBulkEntityDto) => {
        commonService.loading(false);
        this.emailEntitiesObject.part = 2;
        this.emailEntitiesObject.showErrors = true;
        this.emailEntitiesObject.entityStatuses = newEmailEntitiesObject.entityStatuses;
      }), error => {
        this.$errors = error.getMessages();
      });

  };

  @action ok = () => {
    const {entityName} = this.props;
    console.log('Emailing entities ' + this.props.entityName);
    commonService.loading(true);
    gridService.doMultipleEmail(this.emailEntitiesObject, entityName, 2)
      .then(action("email-action", (newEmailEntitiesObject: GridBulkEntityDto) => {
        commonService.loading(false);
        newEmailEntitiesObject.part = 3;
        newEmailEntitiesObject.showErrors = false;
        this.emailEntitiesObject = newEmailEntitiesObject;
      }), error => {
        this.$errors = error.getMessages();
      });
  };

  @action close = () => {
    this.emailEntitiesObject = null;
    this.resolve(null);
  };

  @action cancel = (evt) => {
    console.log('EntitiesEmailDialog#cancel evt = %O', evt);
    this.emailEntitiesObject = null;
    this.reject(null);
  };

  @action onShowErrors = (evt) => {
    this.emailEntitiesObject.showErrors = evt.target.checked;
  };


  @action onCheckIncludeSent = (evt, checked) => {
    const {entityName, documentLocation} = this.props;
    console.log('onCheckIncludeSent checked: ' + checked);
    this.emailEntitiesObject.gridParams._includeSent = checked;
    uiService.showDialogActionFlag();
    documentService.documentCount(entityName, documentLocation, this.emailEntitiesObject.gridParams)
      .then( (documentCountObject: DocumentCountDto) => {
        this.emailEntitiesObject.count = documentCountObject.documentCount;
        uiService.hideDialogActionFlag();
      });
  };

  @action onCheckIncludeAll = (evt, checked) => {
    const {entityName, documentLocation} = this.props;
    console.log('onCheckIncludeAll checked: ' + checked);
    this.emailEntitiesObject.gridParams._includeAll = checked;

    uiService.showDialogActionFlag();
    documentService.documentCount(entityName, documentLocation, this.emailEntitiesObject.gridParams)
      .then( (documentCountObject: DocumentCountDto) => {
        this.emailEntitiesObject.count = documentCountObject.documentCount;
        uiService.hideDialogActionFlag();
      });

  };


  render() {
    const {entityDetailRoute, localizedEntitiesName} = this.props;

    if (!(this.emailEntitiesObject && this.dialogOpen)) return null;

    let issuableCount =  this.emailEntitiesObject.issuableCount();

    let cancelButton = <FlatButton label="Cancel" tabIndex={0} primary onClick={this.cancel} />;

    let showErrorCheckbox = <MdCheckbox label="Show Errors" checked={this.emailEntitiesObject.showErrors} labelStyle={{whiteSpace: 'nowrap'}} style={{display: 'inline-block', width: 140, verticalAlign: 'middle'}} onCheck={this.onShowErrors} />;

    let actions;

    if (this.emailEntitiesObject.part === 1) {
      actions = [cancelButton, <FlatButton label="Prepare Emails" secondary onClick={this.prepare}/>];
    } else if (this.emailEntitiesObject.part === 2) {
      actions = [showErrorCheckbox, cancelButton,
        <FlatButton label="Recheck" secondary onClick={this.prepare}/>, <FlatButton label="Send Emails !" secondary onClick={this.ok} disabled={issuableCount === 0}/>];
    } else if (this.emailEntitiesObject.part === 3) {
      actions = [showErrorCheckbox, <FlatButton label="Close" primary onClick={this.close} />];
    }


    console.debug("rendering EntitiesEmailDialog ", this.emailEntitiesObject);


    return (
      <Dialog
        title={this.titleWithProgress(null)}
        titleStyle={{height: 2, paddingBottom: 0}}
        actions={actions}
        modal={true}
        open={this.dialogOpen}
        contentStyle={{width: '80%', maxWidth: 800}}
        bodyStyle={{overflow: 'visible'}}
      >


        <Stepper activeStep={this.emailEntitiesObject.part - 1}>
          <Step>
            <StepLabel className="mui-stepper-icon-hack"> {`Check (${this.emailEntitiesObject.count})`} </StepLabel>
          </Step>
          <Step>
            <StepLabel className="mui-stepper-icon-hack"> { `Do send emails (${issuableCount})` } </StepLabel>
          </Step>
          <Step>
            <StepLabel className="mui-stepper-icon-hack"> { `Done (${ (this.emailEntitiesObject.part === 3) ? issuableCount : 0})` }  </StepLabel>
          </Step>
        </Stepper>




        <ErrorComp messages={this.$errors}/>

        {
          (this.emailEntitiesObject.part === 1) ?
            <div style={{marginLeft:10}}>
              <MdCheckbox name="includeAll" checked={this.emailEntitiesObject.gridParams._includeAll}
                          label={`Include all ${localizedEntitiesName} (canceled, reopened)`} onCheck={this.onCheckIncludeAll}/>

              <MdCheckbox  name="includeSent" checked={this.emailEntitiesObject.gridParams._includeSent}
                          label={`Include already sent ${localizedEntitiesName}`} onCheck={this.onCheckIncludeSent}/>
            </div>
            : (
            <div style={{overflowY: 'auto', maxHeight:500}}>
              <table className="table table-condensed fullwrap">
                <tbody>
                {this.emailEntitiesObject.entityStatuses.filter( is => this.emailEntitiesObject.showErrors || is.status !== 'ERROR' ).map( (is, index) => {
                  let _className = entityStatusCss(is);
                  return (
                  <tr key={index}>
                    <td>
                      <Link to={entityDetailRoute} params={{id: lovValue(is.entityObject)}}  query={addTenantToQuery()}><span className={_className}>{lovLabel(is.entityObject)}</span></Link>
                      { (is.messages && is.messages.length) ? (
                        <table style={{marginLeft: 20}}>
                          <tbody>
                          {is.messages.map( (messageObj, index) => (
                            <tr key={index}><td className={validationMessageCss(messageObj)}>{messageObj.message}</td></tr>
                          ))}
                          </tbody>
                        </table>
                      ) : null}
                    </td>
                    <td className={_className}>
                      { is.emails.join(', ')}
                    </td>
                    <td className={_className}>
                      {entityStatusText(is)}
                    </td>
                  </tr>
                )
                })}
                </tbody>
              </table>
            </div>
          )
        }



      </Dialog>
    );
  }


}



// WEBPACK FOOTER //
// ./src/invoice/component/entitiesEmailDialog.tsx